import { JAM_EVENT_DETAILS_ROUTES } from '@/src/routes';
import { DashboardJamMiniEvent, JamEventTypes } from '@/src/types/JamDashboard';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { formatJamEventTime } from '@/src/utils/myjam.utils';
import { Box, Link, SpaceBetween, Table } from '@amzn/awsui-components-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface JamListProps {
  events: DashboardJamMiniEvent[];
  eventType: JamEventTypes;
}

const JamList: React.FC<JamListProps> = ({ events, eventType }) => {
  const { t } = useTranslation();

  const getLink = (eventId: string) => {
    let link = '';

    if (eventType === JamEventTypes.PastEvents) {
      link = JAM_EVENT_DETAILS_ROUTES.Report.resolve(eventId);
    } else {
      link = JAM_EVENT_DETAILS_ROUTES.Challenges.resolve(eventId);
    }
    /**
     * there are lots of information saved in the
     * context for existing opened event
     *
     * reloading the page would ensure all the
     * contexts are reset and won't conflict
     * with previous event content
     */
    return link;
  };

  return (
    <Table
      variant="borderless"
      totalItemsCount={Math.max(events.length, 1)} // For accessibility reasons the row count must be a minimum of 1 even when empty, as the empty placeholder is counted as an item
      columnDefinitions={[
        {
          id: 'title',
          header: t(i18nKeys.myJams.eventTabs.table.title),
          cell: ({ title, eventId }) => (
            /**
             * for campaign we can not use slug
             * hence we will always use eventId
             * for campaign events and not slug
             */
            <Link href={getLink(eventId)}>{title}</Link>
          ),
        },
        {
          id: 'progress',
          header: t(i18nKeys.myJams.eventTabs.table.progress),
          cell: ({ progress }) =>
            progress
              ? t(i18nKeys.myJams.eventTabs.table.progressChallenges, {
                  count: progress.solvedChallenges,
                  total: progress.totalChallenges,
                })
              : '--',
        },
        {
          id: 'startTime',
          header: t(i18nKeys.myJams.eventTabs.table.startTime),
          cell: ({ startTime }) => formatJamEventTime(startTime),
        },
        {
          id: 'endTime',
          header: t(i18nKeys.myJams.eventTabs.table.endTime),
          cell: ({ endTime }) => formatJamEventTime(endTime),
        },
      ]}
      items={events}
      empty={
        <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
          <SpaceBetween size="m">
            <b>{t(i18nKeys.myJams.eventTabs.table.emptyListMessage)}</b>
          </SpaceBetween>
        </Box>
      }
    />
  );
};

export default JamList;
